'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/** How long a submit waits for the bot check before going ahead without it. */
const TOKEN_WAIT_MS = 10_000;

/**
 * Submit flow for a form protected by an invisible Turnstile widget. The submit
 * button is never disabled while the check runs: `request()` submits right away
 * when a token is ready, otherwise it waits for one (`waiting`) and submits as
 * soon as it arrives. After 10 seconds without a token it submits with an empty
 * one and lets core decide.
 *
 * Tokens are single-use: each one is spent by the submit it starts, and
 * `reset()` renders a fresh challenge after a rejected submit.
 */
export function useTurnstileSubmit(onReady: (token: string) => void) {
  const [token, setToken] = useState<string | null>(null);
  const [challengeKey, setChallengeKey] = useState(0);
  const [waiting, setWaiting] = useState(false);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  const request = useCallback(() => {
    if (token) {
      setToken(null);
      onReadyRef.current(token);
      return;
    }
    setWaiting(true);
  }, [token]);

  useEffect(() => {
    if (!waiting) return;
    if (token) {
      setWaiting(false);
      setToken(null);
      onReadyRef.current(token);
      return;
    }
    const timer = window.setTimeout(() => {
      setWaiting(false);
      onReadyRef.current('');
    }, TOKEN_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [waiting, token]);

  const reset = useCallback(() => {
    setToken(null);
    setChallengeKey(key => key + 1);
  }, []);

  return {
    /** Pass to TurnstileField's `onToken`. */
    onToken: setToken,
    /** Pass to TurnstileField's `resetSignal`. */
    challengeKey,
    /** A submit is waiting for the bot check. */
    waiting,
    request,
    reset,
  };
}
